// THE MAP WAIT (PC entry; APP_STARTUP §7; owner 2026-10-31 "the initial beam
// from the map to a place is slow, it should show something on the display
// for the user to make the waiting time entertaining"): while the entry's 2D
// stage holds on the beam point (zoom → hold, until the crossfade) the Map
// pushes in and turns slowly about it (FlightCover cine, beamflight.js), the
// names around it pop in one by one (peaks with their altitude, lakes,
// places), the card under the top rotates facts, and the beam point carries a
// light spot with a ring that fills with the landing's real readiness (the
// hold's own terms + its flightTest; never a timer). Only data the world
// ships (the gazetteer, the place reply's ground); nothing fetched. Pure
// parts here (check-mapfirst §5c); main.js wires them, FlightFill draws the names.
import { LABEL_KINDS, readiness } from './beamcharge.js';

// the kinds that pop in: the 3D view's (LABEL_KINDS) + passes and huts (the Map's own, with their altitude)
export const WAIT_KINDS = new Set([...LABEL_KINDS, 'pass', 'alpine_hut']);
const WAIT_PRI = { peak: 0, lake: 1, city: 1, town: 1, pass: 2, village: 2, alpine_hut: 3, hamlet: 3, suburb: 3, place: 3 };
const ELE_KINDS = new Set(['peak', 'pass', 'alpine_hut']);
const PLACE_KINDS = new Set(['city', 'town', 'village', 'hamlet', 'place', 'suburb']);
const DIRS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];

// the compass point from (x, z) to (px, pz): z runs south (the origin NW)
export function compass(x, z, px, pz) {
  const a = Math.atan2(px - x, z - pz);                       // 0 = north, clockwise
  return DIRS[((Math.round(a / (Math.PI / 4)) % 8) + 8) % 8];
}

// the names that pop in: the gazetteer's features of WAIT_KINDS within
// wait_r_m of the beam point (a peak, pass or hut only with its altitude), one per name,
// in pop order — near first, the prominent kinds earlier — at most wait_names
export function waitNames(gaz, x, z, F) {
  if (!gaz) return [];
  const seen = new Set(), out = [];
  const all = gaz.within(x, z, F.wait_r_m, WAIT_KINDS)
    .filter((p) => p.name && (!ELE_KINDS.has(p.kind) || Number.isFinite(p.ele)))
    .map((p) => ({ p, k: p.dist * (1 + 0.5 * (WAIT_PRI[p.kind] ?? 3)) }))
    .sort((a, b) => a.k - b.k);
  for (const { p } of all) {
    if (seen.has(p.name)) continue;
    seen.add(p.name);
    const txt = ELE_KINDS.has(p.kind) ? `${p.name} · ${Math.round(p.ele)} m` : p.name;
    out.push({ name: p.name, kind: p.kind, ele: p.ele, x: p.x, z: p.z, txt });
    if (out.length >= F.wait_names) break;
  }
  return out;
}
// how many of them are in by now (t0: the hold's start; one per wait_name_ms)
export function waitShown(n, t0, now, F) {
  return t0 === undefined || t0 === null ? 0 : Math.min(n, Math.max(0, Math.floor((now - t0) / F.wait_name_ms) + 1));
}

// the facts the card rotates: [{ key, vars }] (i18n keys main.wait.*), each
// only when the data has it — o { gaz, x, z, label }. km: one decimal; dir: a compass key (main.dir.*). The landing's
// altitude is the card's own line (arrivalCard), never a fact twice
export function waitFacts(o, F) {
  const out = [];
  if (!o.gaz) return out;
  const R = F.wait_r_m, km = (d) => (d / 1000).toFixed(1), dir = (p) => compass(o.x, o.z, p.x, p.z);
  const near = o.gaz.within(o.x, o.z, R, null);
  const peaks = near.filter((p) => p.kind === 'peak' && p.name && Number.isFinite(p.ele));
  if (peaks.length) {
    const p = peaks[0];
    out.push({ key: 'main.wait.peak', vars: { name: p.name, m: Math.round(p.ele), km: km(p.dist), dir: dir(p) } });
    const hi = peaks.reduce((a, b) => (b.ele > a.ele ? b : a));
    if (hi !== p) out.push({ key: 'main.wait.highest', vars: { name: hi.name, m: Math.round(hi.ele), km: km(hi.dist), dir: dir(hi) } });
    if (peaks.length >= 2) out.push({ key: 'main.wait.summits', vars: { n: peaks.length, km: km(R) } });
  }
  const lake = near.find((p) => p.kind === 'lake' && p.name);
  if (lake) out.push({ key: 'main.wait.lake', vars: { name: lake.name, km: km(lake.dist), dir: dir(lake) } });
  const place = near.find((p) => PLACE_KINDS.has(p.kind) && p.name && p.name !== o.label);
  if (place) out.push({ key: 'main.wait.place', vars: { name: place.name, km: km(place.dist), dir: dir(place) } });
  return out;
}
// the fact on show by now (one per wait_fact_ms, round), or null
export function waitFact(facts, t0, now, F) {
  if (!facts.length) return null;
  return facts[Math.floor(Math.max(0, now - t0) / F.wait_fact_ms) % facts.length];
}

// the ring: the hold's release terms met (latch, warm-up, top, first acts)
// and the landing pose's test (its drawn share), each a fifth; it never runs
// back (prev), and it is full only when the hold is released NOW (all four +
// the test passing) — the crossfade follows at once
export function waitProgress(k, test, prev = 0) {
  const n = (k.latch ? 1 : 0) + (k.warm ? 1 : 0) + (k.top ? 1 : 0) + (k.acts ? 1 : 0);
  const now = (n + readiness(test)) / 5, ready = n === 4 && !!test && test.ok;
  return ready ? 1 : Math.min(0.99, Math.max(prev, now));
}

// the beam point on the Map: a light spot (pulsing) + the readiness ring
// around it, at (sx, sy) device px; px = device px per CSS px
export function paintBeamSpot(g, sx, sy, px, fill, now) {
  const r = 15 * px, pulse = 0.75 + 0.25 * Math.sin(now / 260);
  g.save();
  g.setTransform(1, 0, 0, 1, 0, 0);
  const glow = g.createRadialGradient(sx, sy, 0, sx, sy, r * 2.2);
  glow.addColorStop(0, `rgba(165,243,252,${(0.95 * pulse).toFixed(3)})`);
  glow.addColorStop(0.25, `rgba(103,232,249,${(0.55 * pulse).toFixed(3)})`);
  glow.addColorStop(1, 'rgba(103,232,249,0)');
  g.fillStyle = glow;
  g.beginPath(); g.arc(sx, sy, r * 2.2, 0, Math.PI * 2); g.fill();
  g.lineWidth = 3 * px;
  g.strokeStyle = 'rgba(15,23,42,.55)';
  g.beginPath(); g.arc(sx, sy, r, 0, Math.PI * 2); g.stroke();
  if (fill > 0) {
    g.strokeStyle = '#67e8f9';
    g.lineCap = 'round';
    g.beginPath(); g.arc(sx, sy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(1, fill)); g.stroke();
  }
  g.restore();
}
